/**
 * Async State Kit：將 ApiError 映射為安全 UI model。
 * 延伸 UX-05-B；不另建平行 error system。
 * 絕不把 raw.message / stack / SQL 放進 user-visible 欄位。
 */
import { isUnsafeInternalErrorMessage } from './safeUserVisibleError';

export const API_ERROR_CODES = Object.freeze({
  NETWORK: 'API_ERROR_NETWORK',
  UNAUTHORIZED: 'API_ERROR_UNAUTHORIZED',
  FORBIDDEN: 'API_ERROR_FORBIDDEN',
  NOT_FOUND: 'API_ERROR_NOT_FOUND',
  VALIDATION: 'API_ERROR_VALIDATION',
  CONFLICT: 'API_ERROR_CONFLICT',
  RATE_LIMITED: 'API_ERROR_RATE_LIMITED',
  UNAVAILABLE: 'API_ERROR_UNAVAILABLE',
  SERVER: 'API_ERROR_SERVER',
  DOMAIN: 'API_ERROR_DOMAIN',
  UNKNOWN: 'API_ERROR_UNKNOWN',
});

const TITLE_KEYS = Object.freeze({
  [API_ERROR_CODES.NETWORK]: 'travel.common.apiError.network.title',
  [API_ERROR_CODES.UNAUTHORIZED]: 'travel.common.apiError.unauthorized.title',
  [API_ERROR_CODES.FORBIDDEN]: 'travel.common.apiError.forbidden.title',
  [API_ERROR_CODES.NOT_FOUND]: 'travel.common.apiError.notFound.title',
  [API_ERROR_CODES.VALIDATION]: 'travel.common.apiError.validation.title',
  [API_ERROR_CODES.CONFLICT]: 'travel.common.apiError.conflict.title',
  [API_ERROR_CODES.RATE_LIMITED]: 'travel.common.apiError.rateLimited.title',
  [API_ERROR_CODES.UNAVAILABLE]: 'travel.common.apiError.unavailable.title',
  [API_ERROR_CODES.SERVER]: 'travel.common.apiError.server.title',
  [API_ERROR_CODES.DOMAIN]: 'travel.common.apiError.domain.title',
  [API_ERROR_CODES.UNKNOWN]: 'travel.common.apiError.unknown.title',
});

const DESCRIPTION_KEYS = Object.freeze({
  [API_ERROR_CODES.NETWORK]: 'travel.common.apiError.network.description',
  [API_ERROR_CODES.UNAUTHORIZED]: 'travel.common.apiError.unauthorized.description',
  [API_ERROR_CODES.FORBIDDEN]: 'travel.common.apiError.forbidden.description',
  [API_ERROR_CODES.NOT_FOUND]: 'travel.common.apiError.notFound.description',
  [API_ERROR_CODES.VALIDATION]: 'travel.common.apiError.validation.description',
  [API_ERROR_CODES.CONFLICT]: 'travel.common.apiError.conflict.description',
  [API_ERROR_CODES.RATE_LIMITED]: 'travel.common.apiError.rateLimited.description',
  [API_ERROR_CODES.UNAVAILABLE]: 'travel.common.apiError.unavailable.description',
  [API_ERROR_CODES.SERVER]: 'travel.common.apiError.server.description',
  [API_ERROR_CODES.DOMAIN]: 'travel.common.apiError.domain.description',
  [API_ERROR_CODES.UNKNOWN]: 'travel.common.apiError.unknown.description',
});

const NETWORK_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ECONNREFUSED',
]);

function readHttpStatus(cause) {
  if (typeof cause?.httpStatus === 'number') return cause.httpStatus;
  if (typeof cause?.response?.status === 'number') return cause.response.status;
  if (typeof cause?.raw?.httpStatus === 'number') return cause.raw.httpStatus;
  return null;
}

function readNetworkCode(cause) {
  if (typeof cause?.networkCode === 'string' && cause.networkCode) return cause.networkCode;
  if (typeof cause?.code === 'string' && cause.code) return cause.code;
  return '';
}

function readDomainKey(cause) {
  const key = cause?.raw?.key ?? cause?.key;
  return typeof key === 'string' && key.trim() ? key.trim() : '';
}

function readBusinessCode(cause) {
  if (typeof cause?.code === 'number') return cause.code;
  if (typeof cause?.raw?.code === 'number') return cause.raw.code;
  return null;
}

function buildModel(code, { httpStatus = null, networkCode = null, domainKey = null, retryable, descriptionKey } = {}) {
  return {
    code,
    httpStatus,
    retryable: Boolean(retryable),
    titleKey: TITLE_KEYS[code],
    descriptionKey: descriptionKey || DESCRIPTION_KEYS[code],
    ...(domainKey ? { domainKey } : {}),
    diagnostic: {
      code: null,
      httpStatus,
      networkCode: networkCode || null,
      key: domainKey || null,
    },
  };
}

function isTranslated(t, key) {
  if (typeof t !== 'function' || !key) return false;
  const translated = t(key);
  return typeof translated === 'string' && translated.length > 0 && translated !== key;
}

/**
 * @param {import('../api/createClient').ApiError|Error|null|undefined} cause
 * @param {{ t?: (key: string) => string }} [options]
 */
export function mapApiErrorToUiModel(cause, options = {}) {
  const { t } = options;
  const httpStatus = readHttpStatus(cause);
  const networkCode = readNetworkCode(cause);
  const domainKey = readDomainKey(cause);
  const businessCode = readBusinessCode(cause);

  // 1) 已知 domain key 且有安全 i18n
  if (domainKey && isTranslated(t, domainKey)) {
    const model = buildModel(API_ERROR_CODES.DOMAIN, {
      httpStatus,
      networkCode,
      domainKey,
      retryable: false,
      descriptionKey: domainKey,
    });
    model.diagnostic.code = businessCode;
    return model;
  }

  // 2) transport / network
  if (
    NETWORK_CODES.has(networkCode)
    || (
      httpStatus == null
      && businessCode == null
      && /network|timeout|failed to fetch/i.test(String(cause?.message || ''))
    )
  ) {
    const model = buildModel(API_ERROR_CODES.NETWORK, {
      httpStatus,
      networkCode,
      domainKey: domainKey || null,
      retryable: true,
    });
    model.diagnostic.code = businessCode;
    return model;
  }

  // 3) HTTP status class
  if (httpStatus === 401) {
    return withBiz(buildModel(API_ERROR_CODES.UNAUTHORIZED, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }
  if (httpStatus === 403) {
    return withBiz(buildModel(API_ERROR_CODES.FORBIDDEN, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }
  if (httpStatus === 404) {
    return withBiz(buildModel(API_ERROR_CODES.NOT_FOUND, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }
  if (httpStatus === 400 || httpStatus === 422) {
    return withBiz(buildModel(API_ERROR_CODES.VALIDATION, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }
  if (httpStatus === 409) {
    return withBiz(buildModel(API_ERROR_CODES.CONFLICT, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }
  if (httpStatus === 429) {
    return withBiz(buildModel(API_ERROR_CODES.RATE_LIMITED, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }
  if (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
    return withBiz(buildModel(API_ERROR_CODES.UNAVAILABLE, { httpStatus, networkCode, domainKey: domainKey || null, retryable: true }), businessCode);
  }
  if (httpStatus != null && httpStatus >= 500) {
    return withBiz(buildModel(API_ERROR_CODES.SERVER, { httpStatus, networkCode, domainKey: domainKey || null, retryable: true }), businessCode);
  }

  // 4) unsafe raw message → generic
  if (isUnsafeInternalErrorMessage(cause?.message)) {
    return withBiz(buildModel(API_ERROR_CODES.UNKNOWN, { httpStatus, networkCode, domainKey: domainKey || null, retryable: false }), businessCode);
  }

  // 5) unknown — 仍不輸出 raw message
  return withBiz(buildModel(API_ERROR_CODES.UNKNOWN, {
    httpStatus,
    networkCode,
    domainKey: domainKey || null,
    retryable: false,
  }), businessCode);
}

function withBiz(model, businessCode) {
  model.diagnostic.code = businessCode;
  return model;
}

/**
 * 將 UI model 解析成可顯示字串（仍不碰 raw.message）。
 */
export function resolveUiErrorCopy(model, t, overrides = {}) {
  const titleKey = overrides.titleKey || model?.titleKey;
  const descriptionKey = overrides.descriptionKey || model?.descriptionKey;
  return {
    title: typeof t === 'function' ? t(titleKey) : titleKey,
    description: typeof t === 'function' ? t(descriptionKey) : descriptionKey,
    retryable: Boolean(model?.retryable),
    code: model?.code,
  };
}
